import React, { useEffect, useRef, useState } from "react";
import { Steps, Upload, Modal, Tooltip } from "antd";
import { Box, CircularProgress } from "@mui/material";
import { InboxOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { CiImport, CiTrash } from "react-icons/ci";
import { ModalService } from "../../styled";
import { uploadDocumentoProps } from "../Imports/service";
import { useEtapasDocumentos } from "./useEtapasDocumentos";
import { useRemocaoDocumento } from "./useRemocaoDocumento";
import { podeRemoverArquivo } from "./fluxosDocumentos";

const { Dragger } = Upload;

/**
 * Modal genérico de importação de documentos obrigatórios de uma OS.
 *
 * @param {string} fluxo - uma das chaves de FLUXOS_DOCUMENTOS
 *   (ver ./fluxosDocumentos.js)
 */
export function ImportacaoDocumentos({
  fluxo,
  open,
  onClose,
  numeroSolicitacao,
  fetchData,
  objDocs,
  setObjDocs,
  fetchListDocumento,
  dados,
  forcarExigenciaIE,
  setModalUploadDoc,
}) {
  const stepRefs = useRef([]);
  const [fileList, setFileList] = useState([]);
  const [loadingDoc] = useState(false);

  const { arquivosEtapas, pendenteIndex, proximaEtapaPendente, regrasRemocao, titulo } = useEtapasDocumentos({
    fluxo,
    dados,
    objDocs,
    forcarExigenciaIE,
  });

  const {
    arquivoEmRemocao,
    modalConfirmarAberto,
    confirmandoRemocao,
    abrirConfirmacao,
    fecharConfirmacao,
    confirmarRemocao,
  } = useRemocaoDocumento({ numeroSolicitacao, setObjDocs, fetchData, fetchListDocumento });

  const jaTevePendenteRef = useRef(false);

  useEffect(() => {
    if (pendenteIndex !== -1 && stepRefs.current[pendenteIndex]) {
      stepRefs.current[pendenteIndex].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [pendenteIndex]);

  // Assim que o último arquivo pendente é importado, fecha o modal sozinho
  // — mas só depois de ter havido de fato uma pendência nesta abertura do
  // modal (senão ele fecharia sozinho logo ao abrir, antes do usuário ver
  // qualquer coisa, caso o cálculo de pendência já chegue em -1 no 1º render).
  useEffect(() => {
    if (!open) {
      jaTevePendenteRef.current = false;
      return;
    }
    if (pendenteIndex !== -1) {
      jaTevePendenteRef.current = true;
    } else if (jaTevePendenteRef.current) {
      onClose(false);
    }
  }, [pendenteIndex, open]);

  const handleUpload = async ({ file, onSuccess, onError }) => {
    if (!proximaEtapaPendente) {
      toast.info("Não há etapas pendentes.");
      return;
    }

    await uploadDocumentoProps(numeroSolicitacao, "/ordem-servico/documento/upload", objDocs, setObjDocs).customRequest({
      file,
      onSuccess,
      onError,
      nomeParaSalvar: proximaEtapaPendente.nome,
    });

    await fetchData();
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    customRequest: handleUpload,
    fileList,
    onChange: (info) => setFileList([...info.fileList]),
  };

  const situacaoId = dados.situacao?.id;

  return (
    <ModalService title={titulo} open={open} onCancel={() => onClose(false)} width={700} footer={null}>
      <div className="padding-10-container">
        {loadingDoc ? (
          <div className="margin-0-auto">
            <Box sx={{ display: "flex" }}>
              <CircularProgress />
            </Box>
          </div>
        ) : (
          <div className="padding-5">
            <div className="display-flex">
              <div className="steps-vertical">
                <Steps
                  direction="vertical"
                  size="small"
                  current={pendenteIndex !== -1 ? pendenteIndex : objDocs?.length || 0}
                  items={arquivosEtapas.map((etapa) => {
                    const caminhoFisicoArquivo = (objDocs || []).find((doc) =>
                      doc.split(/[\\/]/).pop().toLowerCase().includes(etapa.nome.toLowerCase())
                    );

                    const podeRemover =
                      caminhoFisicoArquivo && podeRemoverArquivo(etapa.nome, situacaoId, regrasRemocao);

                    return {
                      title: <span style={{ color: etapa.cor }}>{etapa.status}</span>,
                      description: (
                        <div
                          style={{
                            padding: "5px",
                            borderLeft:
                              etapa.status.includes("isenta") || etapa.status.includes("Conflito")
                                ? `2px dashed ${etapa.cor}`
                                : "none",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <strong style={{ color: "#585858" }}>{etapa.nome}</strong>
                              {etapa.detalhe && (
                                <div style={{ fontSize: "11px", color: etapa.cor, marginTop: "4px" }}>{etapa.detalhe}</div>
                              )}
                            </div>

                            {podeRemover && (
                              <div onClick={() => abrirConfirmacao(caminhoFisicoArquivo)} style={{ cursor: "pointer" }}>
                                {arquivoEmRemocao === caminhoFisicoArquivo ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <CiTrash style={{ color: "black", fontSize: "20px", marginLeft: "10px" }} title="Remover documento" />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    };
                  })}
                />
              </div>

              <div>
                <Dragger {...uploadProps}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Clique ou arraste o arquivo aqui</p>
                  <p className="ant-upload-hint">Para continuar, é essencial que os Documentos necessários sejam importados.</p>
                </Dragger>
              </div>
            </div>

            <Modal
              title="Confirmar Remoção"
              open={modalConfirmarAberto}
              onOk={confirmarRemocao}
              confirmLoading={confirmandoRemocao}
              onCancel={fecharConfirmacao}
              okText="Sim, remover"
              cancelText="Cancelar"
            >
              <p>Tem certeza que deseja remover este documento?</p>
            </Modal>
          </div>
        )}
      </div>
    </ModalService>
  );
}

export default ImportacaoDocumentos;
